import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './AdminSidebar.css';
import { io } from 'socket.io-client';

function AdminSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return; // Don't fetch if no token
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/orders/summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        const pending = data.counts?.Pending || 0;
        const awaiting = data.counts?.['Awaiting Payment'] || 0;
        const confirmed = data.counts?.['Payment Confirmed'] || 0;
        const actionable = pending + awaiting + confirmed;
        setPendingCount(actionable);
        setLowStockCount(data.lowStockCount || 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // Don't run if no token
    
    let alive = true;
    (async () => { if (alive) await fetchCounts(); })();
    const id = setInterval(fetchCounts, 30000);

    // Determine role
    const role = localStorage.getItem('role');
    if (role === 'superadmin') {
      setIsSuperAdmin(true);
    } else {
      // fallback probe
      (async () => {
        try {
          const res2 = await fetch(`${process.env.REACT_APP_API}/api/admin/users/admins`, { headers: { Authorization: `Bearer ${token}` } });
          if (res2.status === 200) setIsSuperAdmin(true);
        } catch {}
      })();
    }

    return () => { alive = false; clearInterval(id); };
  }, [fetchCounts]);

  // Realtime: update counts on admin:summary events
  useEffect(() => {
    const token = localStorage.getItem('token');
    let socket;
    try {
      socket = io(process.env.REACT_APP_API, { auth: { token } });
      socket.on('admin:summary', () => { try { fetchCounts(); } catch {} });
    } catch {}
    return () => { try { socket && socket.disconnect(); } catch {} };
  }, [fetchCounts]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('role');
    navigate('/login', { replace: true });
    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', window.location.href);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <aside className="admin-sidebar">
      <div className="sidebar-logo">
        <Link to="/admin">
          <img src="/images/chiqui_logo.svg" alt="Logo" />
          <span>Chiqui's Garden</span>
        </Link>
      </div>

      <nav className="sidebar-nav">
        <Link to="/admin" className={isActive('/admin') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3h7a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/>
            <path d="M14 3h7a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/>
            <path d="M14 14h7a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z"/>
            <path d="M3 14h7a1 1 0 0 1 1 1v6a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-6a1 1 0 0 1 1-1z"/>
          </svg>
          <span>Dashboard</span>
        </Link>

        <Link to="/admin/orders" className={`orders-link ${isActive('/admin/orders') ? 'active' : ''}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
            <line x1="3" y1="6" x2="21" y2="6"/>
            <path d="M16 10a4 4 0 0 1-8 0"/>
          </svg>
          <span>Orders</span>
          {pendingCount > 0 && <span className="sidebar-badge">{pendingCount}</span>}
        </Link>

        <Link to="/admin/products" className={`products-link ${isActive('/admin/products') ? 'active' : ''}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
            <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
            <line x1="12" y1="22.08" x2="12" y2="12"/>
          </svg>
          <span>Products</span>
          {lowStockCount > 0 && <span className="sidebar-badge">{lowStockCount}</span>}
        </Link>

        <Link to="/admin/customers" className={isActive('/admin/customers') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
            <circle cx="12" cy="7" r="4"/>
          </svg>
          <span>Customers</span>
        </Link>

        <Link to="/admin/reviews" className={isActive('/admin/reviews') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
          </svg>
          <span>Reviews</span>
        </Link>

        <Link to="/admin/customer-concerns" className={isActive('/admin/customer-concerns') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
          </svg>
          <span>Customer Concerns</span>
        </Link>

        <Link to="/admin/notifications" className={isActive('/admin/notifications') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 17H2a3 3 0 0 0 3-3V9a7 7 0 0 1 14 0v5a3 3 0 0 0 3 3zm-8.27 4a2 2 0 0 1-3.46 0"/>
          </svg>
          <span>Notifications</span>
        </Link>

        <Link to="/admin/promos" className={isActive('/admin/promos') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
            <path d="M7 15h.01"/>
            <path d="M11 15h.01"/>
            <path d="M15 15h.01"/>
            <path d="M7 11h10"/>
          </svg>
          <span>Promo Codes</span>
        </Link>

        <Link to="/admin/rewards" className={isActive('/admin/rewards') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="7"/>
            <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/>
          </svg>
          <span>Rewards</span>
        </Link>

        <Link to="/admin/delivery" className={isActive('/admin/delivery') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="3" width="15" height="13" rx="2" ry="2"/>
            <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/>
            <circle cx="5.5" cy="18.5" r="2.5"/>
            <circle cx="18.5" cy="18.5" r="2.5"/>
          </svg>
          <span>Delivery</span>
        </Link>

        <Link to="/admin/reports" className={isActive('/admin/reports') ? 'active' : ''}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18"/>
            <path d="M18 17V9"/>
            <path d="M13 17V5"/>
            <path d="M8 17v-3"/>
          </svg>
          <span>Reports</span>
        </Link>
        
        {isSuperAdmin && (
          <Link to="/admin/super" className={isActive('/admin/super') ? 'active super-link' : 'super-link'}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              <path d="M2 17l10 5 10-5"/>
              <path d="M2 12l10 5 10-5"/>
            </svg>
            <span>Super Admin</span>
          </Link>
        )}
      </nav>

      <div className="sidebar-footer">
        <button className="sidebar-logout" onClick={handleLogout}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
}

export default AdminSidebar;
